import { supabase } from "@/integrations/supabase/client";
import type { Json, Tables, TablesInsert, TablesUpdate } from "@/integrations/supabase/types";

export type ExcerptusTemplate = Tables<"composer_excerptus_templates">;
export type UserTemplate = Tables<"composer_user_templates">;

export type TemplateSource = "blank" | "excerptus" | "user";

/**
 * Reference sources a `reference_picker` wizard field can pull from. Chips
 * carry structured metadata all the way to the Writing Agent's citation slots.
 */
export type ReferenceSource =
  | "excerpts"    // pinned My Excerpts (body + formatted citation)
  | "library"     // My Library items (with CSL citation)
  | "corpus"      // Corpus docs / folders / buckets
  | "notes"       // Composer Notes (plain-text)
  | "podcasts";   // Podcast episodes (with transcript spans)

/** Structured reference chip persisted with wizard answers + brief. */
export type ReferenceChip = {
  kind: ReferenceSource;
  id: string;
  label: string;
  /** How the Writing Agent should treat this reference. */
  usage?: "cite" | "consult" | "quote_verbatim";
};

/**
 * Typed wizard field. Free-text remains the default so existing templates keep
 * working without a data migration (see `parseWizardQuestions`).
 */
export type WizardField =
  | { kind: "text"; multiline?: boolean; maxChars?: number }
  | { kind: "choice"; options: string[]; multi?: boolean }
  | { kind: "number"; min?: number; max?: number; unit?: string }
  | { kind: "slider"; min: number; max: number; step?: number; minLabel?: string; maxLabel?: string }
  | { kind: "date" }
  | { kind: "reference_picker"; sources: ReferenceSource[]; multi?: boolean };

export type WizardQuestion = {
  id: string;
  prompt: string;
  hint?: string | null;
  required?: boolean;
  /** Optional; when absent, treated as `{ kind: "text", multiline: true }`. */
  field?: WizardField;
};

/** Per-section voice steering, layered on top of the avatar's global weight. */
export type VoiceLean = "mode_a" | "mode_b" | "blend";

export type StructureSection = {
  section: string;
  angle?: string | null;
  goal?: string | null;
  /** Optional target word count for this section (used by the Writing Agent). */
  target_word_count?: number | null;
  /** Phrases / facts / references the section MUST include. */
  must_include?: string[] | null;
  /** Phrases / framings the section MUST NOT include. */
  must_avoid?: string[] | null;
  /** Optional voice lean override for this section. */
  voice_lean?: VoiceLean | null;
};

const REFERENCE_SOURCES: ReferenceSource[] = [
  "excerpts",
  "library",
  "corpus",
  "notes",
  "podcasts",
];

const parseField = (raw: unknown): WizardField | undefined => {
  if (!raw || typeof raw !== "object") return undefined;
  const obj = raw as Record<string, unknown>;
  const kind = typeof obj.kind === "string" ? obj.kind : "";
  switch (kind) {
    case "text":
      return {
        kind: "text",
        multiline: typeof obj.multiline === "boolean" ? obj.multiline : true,
        maxChars: typeof obj.maxChars === "number" ? obj.maxChars : undefined,
      };
    case "choice": {
      const options = Array.isArray(obj.options)
        ? obj.options.filter((o): o is string => typeof o === "string")
        : [];
      return { kind: "choice", options, multi: Boolean(obj.multi) };
    }
    case "number":
      return {
        kind: "number",
        min: typeof obj.min === "number" ? obj.min : undefined,
        max: typeof obj.max === "number" ? obj.max : undefined,
        unit: typeof obj.unit === "string" ? obj.unit : undefined,
      };
    case "slider":
      return {
        kind: "slider",
        min: typeof obj.min === "number" ? obj.min : 0,
        max: typeof obj.max === "number" ? obj.max : 100,
        step: typeof obj.step === "number" ? obj.step : undefined,
        minLabel: typeof obj.minLabel === "string" ? obj.minLabel : undefined,
        maxLabel: typeof obj.maxLabel === "string" ? obj.maxLabel : undefined,
      };
    case "date":
      return { kind: "date" };
    case "reference_picker": {
      const sources = Array.isArray(obj.sources)
        ? (obj.sources.filter((s): s is ReferenceSource =>
            typeof s === "string" && (REFERENCE_SOURCES as string[]).includes(s),
          ))
        : [];
      return {
        kind: "reference_picker",
        sources: sources.length > 0 ? sources : ["corpus", "library", "excerpts"],
        multi: obj.multi === undefined ? true : Boolean(obj.multi),
      };
    }
    default:
      return undefined;
  }
};

export const parseWizardQuestions = (value: Json | null | undefined): WizardQuestion[] => {
  if (!Array.isArray(value)) return [];
  return value
    .filter((q) => q && typeof q === "object" && !Array.isArray(q))
    .map((q, i) => {
      const obj = q as Record<string, unknown>;
      return {
        id: typeof obj.id === "string" ? obj.id : `q${i}`,
        prompt: typeof obj.prompt === "string" ? obj.prompt : "",
        hint: typeof obj.hint === "string" ? obj.hint : null,
        required: Boolean(obj.required),
        field: parseField(obj.field),
      };
    })
    .filter((q) => q.prompt.length > 0);
};

export const parseStructure = (value: Json | null | undefined): StructureSection[] => {
  if (!Array.isArray(value)) return [];
  return value
    .filter((s) => s && typeof s === "object" && !Array.isArray(s))
    .map((s) => {
      const obj = s as Record<string, unknown>;
      const mustInclude = Array.isArray(obj.must_include)
        ? obj.must_include.filter((x): x is string => typeof x === "string")
        : null;
      const mustAvoid = Array.isArray(obj.must_avoid)
        ? obj.must_avoid.filter((x): x is string => typeof x === "string")
        : null;
      const lean = obj.voice_lean;
      const voiceLean: VoiceLean | null =
        lean === "mode_a" || lean === "mode_b" || lean === "blend" ? lean : null;
      return {
        section: typeof obj.section === "string" ? obj.section : "",
        angle: typeof obj.angle === "string" ? obj.angle : null,
        goal: typeof obj.goal === "string" ? obj.goal : null,
        target_word_count:
          typeof obj.target_word_count === "number" ? obj.target_word_count : null,
        must_include: mustInclude,
        must_avoid: mustAvoid,
        voice_lean: voiceLean,
      };
    })
    .filter((s) => s.section.length > 0);
};

/** Field kind actually used for rendering / persistence — defaults to text. */
export const wizardFieldKind = (q: WizardQuestion): WizardField["kind"] =>
  q.field?.kind ?? "text";


// ---- Template kind (Static vs Dynamic) ----
//
// A template is "Dynamic" when it defines any wizard questions — selecting it
// pre-fills the Brief intake form and runs the wizard dialog. Otherwise it's
// "Static" — selecting it inserts the structure straight into the Draft Canvas
// with no AI / intake / Brief involved. This is derived in the client; no
// `template_kind` column exists yet (deferred until after the n8n review).
export type TemplateKind = "static" | "dynamic";

export const templateKind = (
  t: { wizard_questions?: Json | null } | null | undefined,
): TemplateKind => {
  const qs = parseWizardQuestions(t?.wizard_questions ?? null);
  return qs.length > 0 ? "dynamic" : "static";
};

// ---- Excerptus templates (admin-curated, read-only for end users) ----

export const fetchPublishedExcerptusTemplates = async (): Promise<ExcerptusTemplate[]> => {
  const { data, error } = await supabase
    .from("composer_excerptus_templates")
    .select("*")
    .eq("is_published", true)
    .order("sort_order", { ascending: true })
    .order("created_at", { ascending: true });
  if (error) throw new Error(error.message);
  return data ?? [];
};

export const fetchAllExcerptusTemplates = async (): Promise<ExcerptusTemplate[]> => {
  const { data, error } = await supabase
    .from("composer_excerptus_templates")
    .select("*")
    .order("sort_order", { ascending: true })
    .order("created_at", { ascending: true });
  if (error) throw new Error(error.message);
  return data ?? [];
};

export const createExcerptusTemplate = async (
  values: Partial<TablesInsert<"composer_excerptus_templates">> & { name: string; content_type: string },
): Promise<ExcerptusTemplate> => {
  const { data: userResp } = await supabase.auth.getUser();
  const payload: TablesInsert<"composer_excerptus_templates"> = {
    name: values.name,
    content_type: values.content_type,
    description: values.description ?? null,
    intro_message: values.intro_message ?? null,
    wizard_questions: values.wizard_questions ?? [],
    default_structure: values.default_structure ?? [],
    is_published: values.is_published ?? false,
    sort_order: values.sort_order ?? 0,
    created_by_user_id: userResp.user?.id ?? null,
  };
  const { data, error } = await supabase
    .from("composer_excerptus_templates")
    .insert(payload)
    .select("*")
    .single();
  if (error) throw new Error(error.message);
  return data;
};

export const updateExcerptusTemplate = async (
  id: string,
  values: TablesUpdate<"composer_excerptus_templates">,
): Promise<ExcerptusTemplate> => {
  const { data, error } = await supabase
    .from("composer_excerptus_templates")
    .update(values)
    .eq("id", id)
    .select("*")
    .single();
  if (error) throw new Error(error.message);
  return data;
};

export const deleteExcerptusTemplate = async (id: string): Promise<void> => {
  const { error } = await supabase.from("composer_excerptus_templates").delete().eq("id", id);
  if (error) throw new Error(error.message);
};

// ---- User templates (workspace-scoped, owner-editable) ----

export const fetchUserTemplates = async (workspaceId: string): Promise<UserTemplate[]> => {
  const { data, error } = await supabase
    .from("composer_user_templates")
    .select("*")
    .eq("workspace_id", workspaceId)
    .order("sort_order", { ascending: true })
    .order("created_at", { ascending: true });
  if (error) throw new Error(error.message);
  return data ?? [];
};

export const createUserTemplate = async (
  workspaceId: string,
  values: { name: string; content_type: string; description?: string | null; intro_message?: string | null; wizard_questions?: Json; default_structure?: Json; cloned_from_excerptus_id?: string | null },
): Promise<UserTemplate> => {
  const { data: userResp } = await supabase.auth.getUser();
  if (!userResp.user) throw new Error("Not signed in");
  const payload: TablesInsert<"composer_user_templates"> = {
    workspace_id: workspaceId,
    created_by_user_id: userResp.user.id,
    name: values.name,
    content_type: values.content_type,
    description: values.description ?? null,
    intro_message: values.intro_message ?? null,
    wizard_questions: values.wizard_questions ?? [],
    default_structure: values.default_structure ?? [],
    cloned_from_excerptus_id: values.cloned_from_excerptus_id ?? null,
  };
  const { data, error } = await supabase
    .from("composer_user_templates")
    .insert(payload)
    .select("*")
    .single();
  if (error) throw new Error(error.message);
  return data;
};

export const updateUserTemplate = async (
  id: string,
  values: TablesUpdate<"composer_user_templates">,
): Promise<UserTemplate> => {
  const { data, error } = await supabase
    .from("composer_user_templates")
    .update(values)
    .eq("id", id)
    .select("*")
    .single();
  if (error) throw new Error(error.message);
  return data;
};

export const deleteUserTemplate = async (id: string): Promise<void> => {
  const { error } = await supabase.from("composer_user_templates").delete().eq("id", id);
  if (error) throw new Error(error.message);
};

/** Persist a new ordering of the workspace's user templates. Updates `sort_order` per id. */
export const reorderUserTemplates = async (
  workspaceId: string,
  orderedIds: string[],
): Promise<void> => {
  await Promise.all(
    orderedIds.map((id, index) =>
      supabase
        .from("composer_user_templates")
        .update({ sort_order: index + 1 })
        .eq("id", id)
        .eq("workspace_id", workspaceId),
    ),
  );
};

export const cloneExcerptusTemplate = async (
  template: ExcerptusTemplate,
  workspaceId: string,
): Promise<UserTemplate> => {
  return createUserTemplate(workspaceId, {
    name: `${template.name} (copy)`,
    content_type: template.content_type,
    description: template.description,
    intro_message: template.intro_message,
    wizard_questions: template.wizard_questions,
    default_structure: template.default_structure,
    cloned_from_excerptus_id: template.id,
  });
};

/** Duplicate one of the user's own templates as a new "(copy)" row. */
export const duplicateUserTemplate = async (
  template: UserTemplate,
): Promise<UserTemplate> => {
  return createUserTemplate(template.workspace_id, {
    name: `${template.name} (copy)`,
    content_type: template.content_type,
    description: template.description,
    intro_message: template.intro_message,
    wizard_questions: template.wizard_questions,
    default_structure: template.default_structure,
    cloned_from_excerptus_id: template.cloned_from_excerptus_id ?? null,
  });
};

/**
 * Replace a template's intro + structure from a TipTap BriefDocument.
 * H1/H2 headings become section names; their following paragraphs become
 * the section's `goal` (truncated). The first italic paragraph becomes the
 * intro_message. Falls back to a single "Body" section when no headings exist.
 */
export const updateUserTemplateFromDoc = async (
  templateId: string,
  doc: unknown,
): Promise<UserTemplate> => {
  const { intro, structure } = briefDocToTemplateStructure(doc);
  const { data, error } = await supabase
    .from("composer_user_templates")
    .update({
      intro_message: intro,
      default_structure: structure as unknown as Json,
    })
    .eq("id", templateId)
    .select("*")
    .single();
  if (error) throw new Error(error.message);
  return data;
};

const briefDocToTemplateStructure = (
  doc: unknown,
): { intro: string | null; structure: StructureSection[] } => {
  const sections: StructureSection[] = [];
  let intro: string | null = null;
  type N = { type?: string; attrs?: { level?: number }; content?: N[]; marks?: { type: string }[]; text?: string };
  const root = doc as N | null;
  const blocks = Array.isArray(root?.content) ? (root!.content as N[]) : [];
  const textOf = (n: N | undefined): string => {
    if (!n) return "";
    if (n.type === "text" && typeof n.text === "string") return n.text;
    return (n.content ?? []).map(textOf).join("");
  };
  let current: StructureSection | null = null;
  const flush = () => { if (current) sections.push(current); current = null; };
  for (const block of blocks) {
    const t = block.type ?? "";
    const text = textOf(block).trim();
    if (!text) continue;
    if (t === "heading") {
      flush();
      current = { section: text.slice(0, 120) };
    } else if (t === "paragraph") {
      const isItalic = (block.content ?? []).every(
        (c) => c.type !== "text" || (c.marks ?? []).some((m) => m.type === "italic"),
      );
      if (!current && intro === null && isItalic) {
        intro = text.slice(0, 400);
      } else if (current) {
        const goal = current.goal ? `${current.goal}\n${text}` : text;
        current.goal = goal.slice(0, 400);
      } else {
        current = { section: "Body", goal: text.slice(0, 400) };
      }
    }
  }
  flush();
  if (sections.length === 0) sections.push({ section: "Body" });
  return { intro, structure: sections };
};

// ---- Common content type options ----

export const CONTENT_TYPE_OPTIONS = [
  { value: "book_chapter", label: "Book chapter" },
  { value: "essay", label: "Essay" },
  { value: "blog_post", label: "Blog post" },
  
  { value: "social_thread", label: "Social thread" },
  { value: "newsletter", label: "Newsletter" },
  { value: "note", label: "Note" },
  { value: "talk_outline", label: "Talk outline" },
  { value: "podcast_outline", label: "Podcast outline" },
  { value: "other", label: "Other" },
] as const;

export const contentTypeLabel = (value: string | null | undefined): string => {
  if (!value) return "Untyped";
  const found = CONTENT_TYPE_OPTIONS.find((o) => o.value === value);
  return found?.label ?? value;
};
